import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { BACKEND_URL } from '@/config';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Clock, Users, BookOpen, Award, ChevronRight,
  Video, FileQuestion, Layers, GripVertical, ListChecks,
  CircleDot, Image, Headphones, Download, PenLine,
  CheckCircle2, Loader2, Play, ShieldCheck, Star,
  BarChart3, Target, MessageCircle, HelpCircle, Package, Building2,
  Heart, Home, Tag,
  Lock,
} from 'lucide-react';
import CourseDiscussions from '@/components/CourseDiscussions';

const ELEMENT_ICONS = {
  video: Video, quiz: FileQuestion, case_study: BookOpen,
  flashcard: Layers, fill_gaps: GripVertical, drag_drop_task: ListChecks,
  matching: CircleDot, image_hotspot: Image, audio: Headphones,
  downloadable: Download, reflection_journal: PenLine, text: BookOpen,
};

const ELEMENT_LABELS = {
  video: 'Video', quiz: 'Quiz', case_study: 'Case Study',
  flashcard: 'Flashcards', fill_gaps: 'Fill the Gaps',
  drag_drop_task: 'Interactive Task', matching: 'Matching Exercise',
  image_hotspot: 'Interactive Image', audio: 'Audio',
  downloadable: 'Resource', reflection_journal: 'Reflection Journal',
  text: 'Reading',
};

const LEVEL_MAP = {
  beginner: { label: 'Beginner', color: 'bg-green-100 text-green-700' },
  intermediate: { label: 'Intermediate', color: 'bg-amber-100 text-amber-700' },
  advanced: { label: 'Advanced', color: 'bg-red-100 text-red-700' },
};

const CourseDetailPage = () => {
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const { api, user, isAuthenticated } = useAuth();

  const [course, setCourse] = useState(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [prereq, setPrereq] = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  useEffect(() => {
    if (isAuthenticated && courseId) {
      api.get(`/courses/${courseId}/enrollment-status`).then(res => {
        setEnrollmentStatus(res.data);
      }).catch(() => {});
      api.get(`/player/prerequisites/${courseId}`).then(res => setPrereq(res.data)).catch(() => setPrereq(null));
      // Check wishlist
      api.get('/wishlist').then(res => {
        setIsWishlisted((res.data || []).includes(courseId));
      }).catch(() => {});
    }
  }, [isAuthenticated, courseId, api]);

  // Track recently viewed in localStorage
  useEffect(() => {
    if (courseId) {
      try {
        const RV_KEY = 'ci_recently_viewed';
        const rv = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
        const filtered = rv.filter(id => id !== courseId);
        filtered.unshift(courseId);
        localStorage.setItem(RV_KEY, JSON.stringify(filtered.slice(0, 10)));
      } catch (e) {
        console.error('Failed to save recently viewed:', e);
      }
    }
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const res = await api.get(`/courses/${courseId}/public`);
      setCourse(res.data);
      // Set document title for SEO
      document.title = res.data.meta_title || res.data.title;
    } catch {
      toast.error('Course not found');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/course/${courseId}`);
      return;
    }
    try {
      setEnrolling(true);
      await api.post(`/player/enroll/${courseId}`);
      toast.success('Enrolled successfully!');
      navigate(`/learn/${courseId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#20acf6]" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-[#2a3e5d]">Course not found</h1>
          <Button asChild className="mt-4"><Link to="/courses">Browse Courses</Link></Button>
        </div>
        <Footer />
      </div>
    );
  }

  const isEnrolled = enrollmentStatus?.enrolled;
  const progressPct = enrollmentStatus?.progress?.overall_progress_pct || 0;
  const orgHasAccess = enrollmentStatus?.org_has_access;
  const isFree = course.is_free || course.price <= 0;
  const isSubscriptionOnly = course.is_subscription_only || false;
  const level = LEVEL_MAP[course.level] || LEVEL_MAP.beginner;
  const durationHours = Math.round(course.duration_minutes / 60 * 10) / 10;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="course-detail-page">
      <Header />

      {/* Breadcrumbs */}
      <nav className="bg-white border-b border-slate-100" data-testid="breadcrumbs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <ol className="flex items-center gap-1.5 text-sm text-slate-500 flex-wrap">
            <li><Link to="/" className="hover:text-[#20acf6] flex items-center gap-1"><Home className="h-3.5 w-3.5" />Home</Link></li>
            <li><ChevronRight className="h-3 w-3 text-slate-300" /></li>
            <li><Link to="/courses" className="hover:text-[#20acf6]">Courses</Link></li>
            {course.category && (
              <>
                <li><ChevronRight className="h-3 w-3 text-slate-300" /></li>
                <li><Link to={`/courses?category=${encodeURIComponent(course.category)}`} className="hover:text-[#20acf6]">{course.category}</Link></li>
              </>
            )}
            <li><ChevronRight className="h-3 w-3 text-slate-300" /></li>
            <li className="text-[#2a3e5d] font-medium truncate max-w-[200px]">{course.title}</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2a3e5d] to-[#2B5A8F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            {/* Left content */}
            <div className="lg:col-span-3 space-y-5">
              <div className="flex flex-wrap gap-2 items-center">
                {course.category && (
                  <Badge className="bg-white/15 text-white border-white/20 text-xs">
                    {course.category}
                  </Badge>
                )}
                <Badge className={`${level.color} text-xs`}>{level.label}</Badge>
                {course.is_accredited && (
                  <Badge className="bg-amber-400/20 text-amber-200 border-amber-400/30 text-xs">
                    <ShieldCheck className="h-3 w-3 mr-1" /> Accredited
                  </Badge>
                )}
                {/* Wishlist button */}
                {isAuthenticated && (
                  <button
                    onClick={async () => {
                      try {
                        if (isWishlisted) {
                          await api.delete(`/wishlist/${courseId}`);
                          setIsWishlisted(false);
                          toast.success('Removed from wishlist');
                        } else {
                          await api.post(`/wishlist/${courseId}`);
                          setIsWishlisted(true);
                          toast.success('Added to wishlist');
                        }
                      } catch { toast.error('Failed to update wishlist'); }
                    }}
                    className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isWishlisted
                        ? 'bg-red-500/20 text-red-200 border border-red-400/30 hover:bg-red-500/30'
                        : 'bg-white/10 text-white/80 border border-white/20 hover:bg-white/20'
                    }`}
                    data-testid="wishlist-detail-btn"
                  >
                    <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-current text-red-300' : ''}`} />
                    {isWishlisted ? 'Saved' : 'Save'}
                  </button>
                )}
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white" data-testid="course-title">
                {course.title}
              </h1>

              {course.short_description && (
                <p className="text-lg text-white/80 leading-relaxed max-w-2xl">
                  {course.short_description}
                </p>
              )}

              {course.instructor_name && (
                <div className="flex items-center gap-3 pt-2 bg-white/5 rounded-xl px-4 py-3" data-testid="course-author-info">
                  <div className="w-11 h-11 rounded-full bg-[#20acf6]/30 flex items-center justify-center text-sm font-bold text-white border-2 border-white/20">
                    {course.instructor_name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs text-white/50 uppercase tracking-wider">Course Author</p>
                    <p className="text-sm font-semibold">{course.instructor_name}</p>
                    {course.instructor_bio && (
                      <p className="text-xs text-white/60 mt-0.5 line-clamp-1">{course.instructor_bio}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Stats row */}
              <div className="flex flex-wrap gap-6 pt-3 text-sm">
                <div className="flex items-center gap-2 text-white/80">
                  <Clock className="h-4 w-4" />
                  <span>{durationHours} hours</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <BookOpen className="h-4 w-4" />
                  <span>{course.total_modules} modules, {course.total_elements} activities</span>
                </div>
                {course.total_videos > 0 && (
                  <div className="flex items-center gap-2 text-white/80">
                    <Video className="h-4 w-4" />
                    <span>{course.total_videos} videos</span>
                  </div>
                )}
                {course.total_quizzes > 0 && (
                  <div className="flex items-center gap-2 text-white/80">
                    <FileQuestion className="h-4 w-4" />
                    <span>{course.total_quizzes} quizzes</span>
                  </div>
                )}
                {course.enrollment_count > 0 && (
                  <div className="flex items-center gap-2 text-white/80">
                    <Users className="h-4 w-4" />
                    <span>{course.enrollment_count} enrolled</span>
                  </div>
                )}
              </div>

              {course.is_accredited && course.accreditation_body && (
                <p className="text-xs text-white/60 pt-1">
                  Accredited by {course.accreditation_body}
                </p>
              )}
            </div>

            {/* Right - CTA Card */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-6 text-slate-900" data-testid="course-cta-card">
                {course.thumbnail_url && (
                  <img
                    src={course.thumbnail_url}
                    alt={course.title}
                    className="w-full h-44 object-cover rounded-xl mb-5"
                  />
                )}

                <PricingSection
                  course={course}
                  isEnrolled={isEnrolled}
                  progressPct={progressPct}
                  orgHasAccess={orgHasAccess}
                  isFree={isFree}
                  isSubscriptionOnly={isSubscriptionOnly}
                  enrolling={enrolling}
                  prereqLocked={!!(prereq && !prereq.met && !isEnrolled)}
                  onEnroll={handleEnroll}
                  isAuthenticated={isAuthenticated}
                  courseId={courseId}
                  fromPreview={new URLSearchParams(window.location.search).get('from') === 'preview'}
                />

                {prereq && !prereq.met && !isEnrolled && (
                  <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm" data-testid="prereq-required-notice">
                    <p className="font-semibold text-amber-900">Complete {prereq.missing.map(m => `"${m.title}"`).join(', ')} first</p>
                    <p className="text-xs text-amber-800 mt-1">This course builds on earlier training. Enrolment opens once you've completed:</p>
                    <ul className="mt-1 space-y-1">
                      {prereq.missing.map(m => (
                        <li key={m.id}><a href={`/course/${m.slug || m.id}`} className="text-[#20acf6] underline text-xs" data-testid={`prereq-link-${m.id}`}>{m.title}</a></li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-5 pt-5 border-t border-slate-100 space-y-3 text-sm text-slate-600">
                  {course.has_free_preview && !isEnrolled && (
                    <Button
                      asChild variant="outline"
                      className="w-full border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5 mb-2"
                      data-testid="try-free-preview-btn"
                    >
                      <Link to={`/course/${courseId}/preview`}>
                        <Play className="h-4 w-4 mr-2" /> Try First Module Free
                      </Link>
                    </Button>
                  )}
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Full lifetime access</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Certificate on completion</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Mobile friendly</div>
                  {course.is_accredited && (
                    <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> CPD accredited</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            {/* About */}
            {course.description && (
              <section>
                <h2 className="text-xl font-bold text-[#2a3e5d] mb-4">About This Course</h2>
                <div className="text-slate-700 leading-relaxed whitespace-pre-line">
                  {course.description}
                </div>
              </section>
            )}

            {/* Target Audience */}
            {course.target_audience && (
              <section data-testid="target-audience">
                <h2 className="text-xl font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#20acf6]" /> Who This Course Is For
                </h2>
                <div className="p-5 bg-[#f8fafc] rounded-xl text-slate-700">
                  {course.target_audience}
                </div>
              </section>
            )}

            {/* Learning Outcomes */}
            {course.learning_outcomes?.length > 0 && (
              <section data-testid="learning-outcomes">
                <h2 className="text-xl font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[#20acf6]" /> What You'll Learn
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {course.learning_outcomes.map((outcome, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                      <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{outcome}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Course Modules */}
            {course.modules?.length > 0 && (
              <section data-testid="course-modules">
                <h2 className="text-xl font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-[#20acf6]" /> Course Content
                </h2>
                <p className="text-sm text-slate-500 mb-4">
                  {course.total_modules} modules &middot; {course.total_elements} activities &middot; {durationHours} hours
                </p>
                <Accordion type="multiple" className="space-y-2">
                  {course.modules.map((mod, mi) => (
                    <AccordionItem key={mod.id} value={mod.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                      <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-slate-50">
                        <div className="flex items-center gap-3 text-left">
                          <span className="w-7 h-7 rounded-lg bg-[#f8fafc] flex items-center justify-center text-xs font-bold text-[#2a3e5d]">
                            {mi + 1}
                          </span>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900 text-sm">{mod.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{mod.element_count} activities</p>
                          </div>
                          {mod.is_free_preview && (
                            <Badge className="bg-[#10B981]/15 text-[#10B981] text-[10px] shrink-0" data-testid={`free-badge-${mod.id}`}>Free Preview</Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-4">
                        {mod.description && (
                          <p className="text-sm text-slate-600 mb-3">{mod.description}</p>
                        )}
                        <div className="space-y-1.5">
                          {mod.elements.map((el) => {
                            const Icon = ELEMENT_ICONS[el.type] || BookOpen;
                            return (
                              <div key={el.id} className="flex items-center gap-3 py-2 px-3 rounded-lg text-sm text-slate-700 bg-slate-50">
                                <Icon className="h-4 w-4 text-[#20acf6] shrink-0" />
                                <span className="flex-1">{el.title}</span>
                                <span className="text-xs text-slate-400">{ELEMENT_LABELS[el.type] || el.type}</span>
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            )}

            {/* FAQs */}
            {course.faqs?.length > 0 && (
              <section data-testid="course-faqs">
                <h2 className="text-xl font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-[#20acf6]" /> Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-2">
                  {course.faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                      <AccordionTrigger className="px-5 py-4 text-left text-sm font-medium hover:no-underline hover:bg-slate-50">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-4 text-sm text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            )}

            {/* Reviews & Ratings */}
            <CourseReviewsSection courseId={courseId} isEnrolled={isEnrolled} isAuthenticated={isAuthenticated} />

            {/* Discussions */}
            <CourseDiscussions courseId={courseId} />
          </div>

          {/* Sticky sidebar for desktop */}
          <div className="hidden lg:block">
            <div className="sticky top-8 space-y-6">
              {/* Prerequisites */}
              {course.prerequisites?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-900 mb-3 text-sm">Prerequisites</h3>
                  <ul className="space-y-2">
                    {course.prerequisites.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <ChevronRight className="h-4 w-4 text-[#20acf6] shrink-0 mt-0.5" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tags */}
              {course.tags?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-900 mb-3 text-sm">Topics</h3>
                  <div className="flex flex-wrap gap-2">
                    {course.tags.map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Share / Social */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-900 mb-3 text-sm">Share This Course</h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Link copied!');
                    }}
                    data-testid="copy-link-btn"
                  >
                    Copy Link
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// ============================================================
// REVIEWS SECTION
// ============================================================
const TRUSTPILOT_URL = "https://www.trustpilot.com/review/commonimpactelearning.co.uk";

const StarRating = ({ rating, onRate, interactive, size = 'h-5 w-5' }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => interactive && onRate?.(star)}
        className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
        disabled={!interactive}
      >
        <Star className={`${size} ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
      </button>
    ))}
  </div>
);

const CourseReviewsSection = ({ courseId, isEnrolled, isAuthenticated }) => {
  const { api } = useAuth();
  const [reviews, setReviews] = useState({ reviews: [], total: 0, average_rating: 0 });
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reviews/${courseId}`).then(r => setReviews(r.data)).catch(() => {});
    if (isAuthenticated) {
      api.get(`/reviews/${courseId}/my-review`).then(r => {
        if (r.data?.rating) {
          setMyRating(r.data.rating);
          setMyComment(r.data.comment || '');
          setSubmitted(true);
        }
      }).catch(() => {});
    }
  }, [courseId, isAuthenticated]);

  const handleSubmit = async () => {
    if (!myRating) return;
    setSubmitting(true);
    try {
      await api.post(`/reviews/${courseId}`, { rating: myRating, comment: myComment });
      setSubmitted(true);
      const r = await axios.get(`${BACKEND_URL}/api/reviews/${courseId}`);
      setReviews(r.data);
    } catch {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="course-reviews">
      <h2 className="text-xl font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
        <Star className="h-5 w-5 text-[#20acf6]" /> Reviews & Ratings
      </h2>

      {/* Summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="text-center">
            <p className="text-3xl font-bold text-[#2a3e5d]">{reviews.average_rating || '—'}</p>
            <StarRating rating={Math.round(reviews.average_rating)} size="h-4 w-4" />
            <p className="text-xs text-slate-500 mt-1">{reviews.total} review{reviews.total !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex-1 min-w-0">
            <a
              href={TRUSTPILOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#00b67a] text-white rounded-lg text-sm font-semibold hover:bg-[#009e68] transition-colors"
              data-testid="trustpilot-cta"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
              Review us on Trustpilot
            </a>
            <p className="text-xs text-slate-500 mt-2">Your feedback helps other care workers find the right training</p>
          </div>
        </div>
      </div>

      {/* Submit review form */}
      {isAuthenticated && isEnrolled && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="review-form">
          <h3 className="text-sm font-semibold text-[#2a3e5d] mb-3">
            {submitted ? 'Your Review' : 'Rate This Course'}
          </h3>
          <div className="flex items-center gap-3 mb-3">
            <StarRating rating={myRating} onRate={setMyRating} interactive={!submitted} size="h-6 w-6" />
            {myRating > 0 && <span className="text-sm text-slate-500">{['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][myRating]}</span>}
          </div>
          {!submitted ? (
            <>
              <textarea
                value={myComment}
                onChange={(e) => setMyComment(e.target.value)}
                placeholder="Share your experience (optional)..."
                className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-[#20acf6] focus:border-transparent"
                rows={3}
                maxLength={500}
                data-testid="review-comment"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-slate-400">{myComment.length}/500</span>
                <Button
                  onClick={handleSubmit}
                  disabled={!myRating || submitting}
                  className="bg-[#20acf6] hover:bg-[#3bb8f7]"
                  size="sm"
                  data-testid="submit-review-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-green-600">Thank you for your review!</p>
          )}
        </div>
      )}

      {/* Reviews list */}
      {reviews.reviews.length > 0 && (
        <div className="space-y-3">
          {reviews.reviews.slice(0, 5).map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#20acf6]/10 flex items-center justify-center text-xs font-bold text-[#20acf6]">
                    {(r.user_name || '?')[0]}
                  </div>
                  <span className="text-sm font-medium text-[#2a3e5d]">{r.user_name || 'Learner'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <StarRating rating={r.rating} size="h-3.5 w-3.5" />
                  <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              {r.comment && <p className="text-sm text-slate-600">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ============================================================
// PRICING / CTA SECTION
// ============================================================
const PricingSection = ({
  course, isEnrolled, progressPct, orgHasAccess,
  isFree, isSubscriptionOnly, enrolling, onEnroll, isAuthenticated, courseId, fromPreview, prereqLocked,
}) => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await api.post('/promo-codes/validate', {
        code: promoCode.trim(),
        course_id: courseId,
        original_price: course.price || 0,
      });
      setPromoResult(res.data);
      toast.success(`Discount applied: ${res.data.discount_type === 'percentage' ? res.data.discount_value + '% off' : '£' + res.data.discount_amount.toFixed(2) + ' off'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
      setPromoResult(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setPromoResult(null);
    setPromoCode('');
  };

  // Already enrolled
  if (isEnrolled) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-600">Your progress</span>
          <span className="text-sm font-medium">{Math.round(progressPct)}%</span>
        </div>
        <Progress value={progressPct} className="h-2.5" />
        <Button
          className="w-full bg-[#20acf6] hover:bg-[#3bb8f7] h-12 text-base"
          onClick={() => navigate(`/learn/${courseId}`)}
          data-testid="continue-learning-btn"
        >
          <Play className="h-5 w-5 mr-2" /> Continue Learning
        </Button>
      </div>
    );
  }

  // From preview highlight
  const ctaPrefix = fromPreview ? 'Continue your learning — ' : '';
  if (prereqLocked) {
    return (
      <Button disabled className="w-full min-h-[48px]" data-testid="enroll-locked-btn">
        <Lock className="h-4 w-4 mr-2" /> Complete required course(s) first
      </Button>
    );
  }

  const currencySymbol = course.currency === 'GBP' ? '£' : course.currency === 'EUR' ? '€' : '$';
  const hasPrice = course.price > 0;
  const displayPrice = promoResult ? promoResult.final_price : course.price;
  const courseBundles = course.bundles || [];
  const tierNames = course.subscription_tier_names || [];

  return (
    <div className="space-y-4" data-testid="course-pricing-panel">

      {/* 1. Subscription Access */}
      {orgHasAccess && (
        <div className="p-3 bg-green-50 rounded-xl border border-green-200 flex items-center gap-3" data-testid="subscription-access-badge">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-green-800">Included in your subscription</p>
            {tierNames.length > 0 && <p className="text-xs text-green-700 mt-0.5">{tierNames.join(', ')}</p>}
          </div>
        </div>
      )}

      {/* 2. Subscription Tiers (when NOT a subscriber) */}
      {!orgHasAccess && tierNames.length > 0 && (
        <div className="p-3 bg-[#F8FAFC] rounded-xl border border-slate-200" data-testid="subscription-tiers-info">
          <p className="text-xs font-medium text-slate-500 mb-1.5">Included in subscriptions:</p>
          <div className="flex flex-wrap gap-1.5">
            {tierNames.map((name, i) => (
              <span key={i} className="inline-flex items-center text-[11px] bg-green-100 text-green-800 rounded-full px-2.5 py-0.5 font-medium">{name}</span>
            ))}
          </div>
          <Link to="/pricing" className="text-xs text-[#20acf6] hover:underline mt-2 inline-block">View subscription plans &rarr;</Link>
        </div>
      )}

      {/* 3. Individual Price */}
      {isSubscriptionOnly && !orgHasAccess ? (
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200" data-testid="subscribe-to-access">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="h-5 w-5 text-amber-600" />
            <p className="text-sm font-semibold text-amber-800">Subscribe to Access</p>
          </div>
          <p className="text-xs text-amber-700 mt-1">This course is only available through an organisation subscription.</p>
          <Link to="/pricing" className="inline-flex items-center gap-1 text-xs text-[#20acf6] hover:underline mt-2 font-medium">
            View subscription plans <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      ) : hasPrice ? (
        <div className="p-3 bg-white rounded-xl border border-slate-200" data-testid="individual-price">
          <p className="text-xs text-slate-500 mb-1">Buy individually</p>
          {promoResult ? (
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-[#2a3e5d]">{currencySymbol}{promoResult.final_price.toFixed(2)}</p>
              <p className="text-sm text-slate-400 line-through">{currencySymbol}{course.price.toFixed(2)}</p>
              <Badge className="bg-green-100 text-green-700 text-[10px]">-{promoResult.discount_type === 'percentage' ? promoResult.discount_value + '%' : currencySymbol + promoResult.discount_amount.toFixed(2)}</Badge>
            </div>
          ) : (
            <p className="text-2xl font-bold text-[#2a3e5d]">{currencySymbol}{course.price.toFixed(2)}</p>
          )}
        </div>
      ) : isFree ? (
        <div className="p-3 bg-white rounded-xl border border-slate-200" data-testid="free-price">
          <p className="text-2xl font-bold text-[#2a3e5d]">Free</p>
        </div>
      ) : !orgHasAccess ? (
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200" data-testid="contact-price">
          <p className="text-sm font-medium text-slate-700">Contact us for pricing</p>
          <p className="text-xs text-slate-500 mt-0.5">Or access via an organisation subscription</p>
        </div>
      ) : null}

      {/* 4. Bundle Options */}
      {courseBundles.length > 0 && (
        <div className="space-y-2" data-testid="bundle-options">
          <p className="text-xs font-medium text-slate-500">Save with a bundle:</p>
          {courseBundles.map(b => {
            const savings = hasPrice && b.full_price > 0
              ? Math.round((1 - b.full_price / (course.price * (b.course_count || 1))) * 100)
              : null;
            return (
              <Link key={b.id} to="/pricing" className="flex items-center gap-2.5 p-3 bg-[#02d1d4]/5 rounded-xl border border-[#02d1d4]/20 hover:border-[#02d1d4]/40 transition-colors text-sm group" data-testid={`bundle-link-${b.id}`}>
                <Package className="h-4 w-4 text-[#02d1d4] shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-[#2a3e5d] font-medium group-hover:text-[#20acf6] truncate block">{b.title}</span>
                  <span className="text-xs text-slate-500">
                    From {currencySymbol}{b.subscriber_price || b.full_price}
                    {b.savings_text && <span className="text-[#02d1d4] ml-1.5 font-medium">{b.savings_text}</span>}
                  </span>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
              </Link>
            );
          })}
        </div>
      )}

      {/* 5. Promo Code Input */}
      {hasPrice && !isSubscriptionOnly && isAuthenticated && (
        <div className="space-y-2" data-testid="promo-code-section">
          {promoResult ? (
            <div className="flex items-center gap-2 p-2.5 bg-green-50 rounded-lg border border-green-200">
              <Tag className="h-4 w-4 text-green-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold text-green-800">{promoResult.code}</span>
                <span className="text-xs text-green-600 ml-1">applied</span>
              </div>
              <button onClick={removePromo} className="text-xs text-green-600 hover:text-green-800 underline" data-testid="remove-promo-btn">Remove</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                value={promoCode}
                onChange={e => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Promo code"
                className="flex-1 text-xs font-mono uppercase h-9"
                onKeyDown={e => e.key === 'Enter' && applyPromo()}
                data-testid="promo-code-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={applyPromo}
                disabled={promoLoading || !promoCode.trim()}
                className="h-9 text-xs shrink-0"
                data-testid="apply-promo-btn"
              >
                {promoLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* 6. Action Buttons */}
      <div className="space-y-2 pt-1">
        {orgHasAccess && (
          <Button
            className="w-full bg-green-600 hover:bg-green-700 h-11 text-sm"
            onClick={onEnroll}
            disabled={enrolling}
            data-testid="enroll-sub-btn"
          >
            {enrolling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Access via Your Subscription
          </Button>
        )}
        {hasPrice && !isSubscriptionOnly && (
          <Button
            className={`w-full h-11 text-sm ${orgHasAccess ? 'bg-[#2a3e5d] hover:bg-[#1e293b]' : 'bg-[#20acf6] hover:bg-[#3bb8f7]'}`}
            onClick={onEnroll}
            disabled={enrolling}
            data-testid="buy-course-btn"
          >
            {enrolling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isAuthenticated
              ? `${ctaPrefix}Buy Now — ${currencySymbol}${displayPrice.toFixed(2)}`
              : `Sign Up & Buy — ${currencySymbol}${course.price.toFixed(2)}`
            }
          </Button>
        )}
        {isFree && !orgHasAccess && (
          <Button
            className="w-full bg-[#02d1d4] hover:bg-[#02bfc2] h-11 text-sm shadow-lg shadow-[#02d1d4]/20"
            onClick={() => isAuthenticated ? onEnroll() : (window.location.href = `/register?redirect=/courses/${course.id}`)}
            disabled={enrolling}
            data-testid="try-free-btn"
          >
            {enrolling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            {isAuthenticated ? 'Try Free' : 'Sign Up & Try Free'}
          </Button>
        )}
        {!hasPrice && !isFree && !orgHasAccess && (
          <Button asChild className="w-full bg-[#20acf6] hover:bg-[#3bb8f7] h-11 text-sm">
            <Link to="/contact">Contact Us</Link>
          </Button>
        )}
      </div>
    </div>
  );
};

export default CourseDetailPage;
